import { AppIcon } from '@/components/ui/app-icon';
import {
  CsvLogoIcon,
  LinkedinLogoIcon,
} from '@/components/actions-drawer/action-icons';
import { ChatSearchBox } from '@/components/ai-search/chat-search-box.component';
import { ListSelectionPopover } from '@/components/list-selection-popover.component';
import { Logo } from '@/components/logo.component';
import { LogoLight } from '@/components/logo-light.component';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCrm } from '@/hooks/use-crm.hook';
import { useLeadLists } from '@/hooks/use-lead-lists.hook';
import { ExtensionDetector } from '@/lib/extension-detector';
import { cn } from '@/lib/utils';
import { useAppDispatch } from '@/store';
import {
  openAiChatConversation,
  setAiChatInitialMessage,
  setAiChatOpen,
} from '@/store/features/ui.feature';
import { useNavigate } from '@tanstack/react-router';
import { landingSearchHandoff } from '@/containers/search/landing-search-handoff';
import {
  announceCopilotSearchRefusal,
  useCopilotSearch,
} from '@/hooks/use-copilot-search.hook';
type IconShim = ComponentType<{
  className?: string;
  style?: AppIconProps['style'];
}>;
const Download: IconShim = (props) => (
  <AppIcon icon={iconDownload} {...props} />
);
const Settings: IconShim = (props) => <AppIcon icon={iconGear} {...props} />;
const SquarePen: IconShim = (props) => <AppIcon icon={iconPencil} {...props} />;
const UserSearch: IconShim = (props) => (
  <AppIcon icon={iconUserMagnifyingGlass} {...props} />
);
const Zap: IconShim = (props) => <AppIcon icon={iconBolt} {...props} />;
import { AnimatePresence, motion } from 'framer-motion';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
} from 'react';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { useTheme } from '@/hooks/use-theme.hook';
import { useGetPlansQuery } from '@/store/features/billing.feature';
import {
  Feature,
  FEATURE_REQUIRED_TIER,
  TIER_LABELS,
} from '@/constants/features';
import type { FeatureKey } from '@/constants/features';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  iconBolt,
  iconChrome,
  iconCircleArrowUp,
  iconCircleCheck,
  iconDownload,
  iconFolderPlus,
  iconGear,
  iconKey,
  iconLink,
  iconPencil,
  iconSpinnerThird,
  iconUserMagnifyingGlass,
  iconWandMagicSparkles,
} from '@/lib/icons';
import type { AppIconProps } from '@/components/ui/app-icon';
import { BRAND } from '@/lib/brand';

// Map source tile IDs to their required feature (undefined = free for all)
const SOURCE_TILE_FEATURE: Record<string, FeatureKey | undefined> = {
  'people-search': Feature.DATA_SOURCE_PEOPLE_SEARCH,
  hubspot: Feature.DATA_SOURCE_CRM_IMPORT,
  linkedin: undefined,
  'sales-nav': Feature.DATA_SOURCE_SALES_NAV,
  csv: Feature.DATA_SOURCE_CSV_UPLOAD,
  manual: undefined,
};

export const ProspectContainer = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { hasFeature, organizationId } = useFeatureAccess();
  const { theme } = useTheme();
  const { currentData: plans } = useGetPlansQuery(
    { organizationId },
    { skip: !organizationId }
  );
  const hasAppSumo = plans?.appsumo != null;
  const isGatedForOrg = (feature: FeatureKey | undefined): boolean => {
    if (!feature) return false;
    if (hasFeature(feature)) return false;
    return true;
  };
  const isTileGated = (tileId: string): boolean => {
    // AppSumo gets People Finder via a metered allowance, so never gate its shortcut.
    if (tileId === 'people-search' && hasAppSumo) return false;
    return isGatedForOrg(SOURCE_TILE_FEATURE[tileId]);
  };
  const { createList, isCreating } = useLeadLists();
  const { crmOptions } = useCrm();
  const copilotSearch = useCopilotSearch();
  // The page was left: a question that ends after that (leaving cancels it,
  // which reads as no result) opens nothing and asks no chat.
  const leftPageRef = useRef(false);
  useEffect(() => {
    leftPageRef.current = false;
    return () => {
      leftPageRef.current = true;
    };
  }, []);
  const [isExtensionInstalled, setIsExtensionInstalled] = useState<
    boolean | null
  >(null);
  const [launchedQuery, setLaunchedQuery] = useState<string | null>(null);
  const [hubspotLogoFailed, setHubspotLogoFailed] = useState(false);
  const [hubspotDialogOpen, setHubspotDialogOpen] = useState(false);
  const [extensionDialogMode, setExtensionDialogMode] = useState<
    'install' | 'api-key' | null
  >(null);
  const [newListDialogOpen, setNewListDialogOpen] = useState(false);
  const [findProspectsDialogOpen, setFindProspectsDialogOpen] = useState(false);
  const [newListId, setNewListId] = useState<string | null>(null);
  const [newListName, setNewListName] = useState('New List');

  // Navigate to list with drawer open (used when list already exists, e.g., from "Find Prospects" dialog)
  const handleCSVEnrich = (listId?: string) => {
    if (listId) {
      navigate({
        to: `/lists/${listId}`,
        search: { openDrawer: 'import-csv' },
      });
    }
  };
  const handleManualEnrich = (listId?: string) => {
    if (listId) {
      navigate({
        to: `/lists/${listId}`,
        search: { openDrawer: 'manual-entry' },
      });
    }
  };
  const handleHubspotEnrich = (listId?: string) => {
    if (listId) {
      navigate({
        to: `/lists/${listId}`,
        search: { openDrawer: 'import-crm' },
      });
    }
  };
  // A create takes several seconds; repeat clicks must not make more lists.
  const peopleSearchInFlightRef = useRef(false);
  const handlePeopleSearch = async (existingListId?: string) => {
    if (existingListId) {
      navigate({ to: '/finder', search: { listId: existingListId } });
      return;
    }
    if (peopleSearchInFlightRef.current) return;
    peopleSearchInFlightRef.current = true;
    try {
      const createdList = await createList({
        name: `Search ${new Date().toLocaleDateString()}`,
        description: 'Created from Find People',
        origin: 'people_search',
      });
      if (createdList?.id) {
        navigate({
          to: '/finder',
          search: { listId: createdList.id },
        });
      }
    } catch {
      // Error handled by hook toast
    } finally {
      peopleSearchInFlightRef.current = false;
    }
  };

  useEffect(() => {
    let isMounted = true;

    ExtensionDetector.isExtensionInstalled().then((installed) => {
      if (isMounted) {
        setIsExtensionInstalled(installed);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const hubspotConnected = crmOptions?.some(
    (crm) => crm.type === 'hubspot' && crm.onboarded
  );
  const hubspotLogoUrl =
    crmOptions?.find((crm) => crm.type === 'hubspot')?.logo_url ||
    'https://cdn.simpleicons.org/hubspot/ff7a59';

  useEffect(() => {
    setHubspotLogoFailed(false);
  }, [hubspotLogoUrl]);

  const ensureLinkedInReady = async () => {
    let extensionInstalled = isExtensionInstalled;

    if (extensionInstalled === null) {
      extensionInstalled = await ExtensionDetector.isExtensionInstalled();
      setIsExtensionInstalled(extensionInstalled);
    }

    if (!extensionInstalled) {
      setExtensionDialogMode('install');
      return false;
    }

    return true;
  };

  const handleHubspotImport = () => {
    if (!hubspotConnected) {
      setHubspotDialogOpen(true);
      return;
    }

    navigate({ to: '/integrations', search: { importCrm: 'hubspot' } });
  };

  const handleLinkedInFind = async () => {
    const isReady = await ensureLinkedInReady();
    if (!isReady) return;
    window.open(
      'https://www.linkedin.com/search/results/people/',
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleSalesNavigatorFind = async () => {
    const isReady = await ensureLinkedInReady();
    if (!isReady) return;
    window.open(
      'https://www.linkedin.com/sales/search/people?viewAllFilters=true',
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleCreateNewList = async () => {
    const listName = newListName.trim() || 'New List';
    try {
      const createdList = await createList({
        name: listName,
        description: 'Created from Find Prospects',
        origin: 'manual',
      });

      if (!createdList?.id) {
        return;
      }

      setNewListId(createdList.id);
      setNewListName(createdList.name || listName);
      setNewListDialogOpen(false);
      setFindProspectsDialogOpen(true);
    } catch (error) {
      console.error(error);
    }
  };

  const extensionDialogContent = useMemo(() => {
    if (extensionDialogMode === 'install') {
      return {
        title: `${BRAND.dotName} Extension Required`,
        description: `LinkedIn prospecting requires the ${BRAND.dotName} chrome extension. Install it before continuing.`,
        actionLabel: 'Open Chrome Web Store',
        action: () =>
          window.open(
            'https://chromewebstore.google.com/detail/cleanlistai/njbepggbhklfpljfcmpnonjdolibjadj',
            '_blank',
            'noopener,noreferrer'
          ),
      };
    }

    if (extensionDialogMode === 'api-key') {
      return {
        title: 'Connect your extension API key',
        description:
          'Generate an API key and add it to the extension settings to link your account.',
        actionLabel: 'Open Extension Setup',
        action: () =>
          navigate({ to: '/integrations', search: { tab: 'extension' } }),
      };
    }

    return null;
  }, [extensionDialogMode, navigate]);

  const getOptionAction = (
    optionId: string,
    listId?: string,
    onAfterAction?: () => void
  ) => {
    switch (optionId) {
      case 'hubspot':
        return () => {
          // If we have a listId (from "Find Prospects" dialog), go directly to that list
          if (listId) {
            handleHubspotEnrich(listId);
          } else {
            handleHubspotImport();
          }
          onAfterAction?.();
        };
      case 'linkedin':
        return async () => {
          await handleLinkedInFind();
          onAfterAction?.();
        };
      case 'sales-nav':
        return async () => {
          await handleSalesNavigatorFind();
          onAfterAction?.();
        };
      case 'csv':
        return () => {
          handleCSVEnrich(listId);
          onAfterAction?.();
        };
      case 'manual':
        return () => {
          handleManualEnrich(listId);
          onAfterAction?.();
        };
      case 'people-search':
        return async () => {
          await handlePeopleSearch(listId);
          onAfterAction?.();
        };
      default:
        return () => undefined;
    }
  };

  // Map option IDs to drawer panel names for popover-based flow
  const optionToPanel: Record<
    string,
    'import-csv' | 'manual-entry' | 'import-crm'
  > = {
    csv: 'import-csv',
    manual: 'manual-entry',
    hubspot: 'import-crm',
  };

  // Compact source tile data - colors mapped to CSS variable strings.
  // The tile renderer uses inline `color` (legacy) so we provide a hsl()
  // wrapper for the brand tokens defined in src/index.css.
  const sourceTiles = [
    {
      id: 'people-search' as const,
      label: 'Find People',
      color: 'hsl(var(--brand-apollo))',
      icon: UserSearch,
    },
    {
      id: 'hubspot' as const,
      label: 'HubSpot',
      color: 'hsl(var(--brand-hubspot))',
      logoType: 'hubspot' as const,
    },
    {
      id: 'linkedin' as const,
      label: 'LinkedIn',
      color: 'hsl(var(--brand-linkedin-alt))',
      logoType: 'linkedin' as const,
    },
    {
      id: 'sales-nav' as const,
      label: 'Sales Nav',
      color: 'hsl(var(--brand-linkedin-alt))',
      logoType: 'sales-nav' as const,
    },
    {
      id: 'csv' as const,
      label: 'CSV',
      color: 'hsl(var(--success))',
      logoType: 'csv' as const,
    },
    {
      id: 'manual' as const,
      label: 'Manual',
      color: 'hsl(var(--brand-lemlist))',
      icon: SquarePen,
    },
    // Sales Nav export is closed to new orgs; hide it rather than upsell it.
  ].filter(
    (tile) =>
      tile.id !== 'sales-nav' || hasFeature(Feature.DATA_SOURCE_SALES_NAV)
  );

  return (
    <>
      <div className='space-y-3 px-4 pt-4 lg:px-6 lg:pt-6'>
        {/* AppSumo header — the "Prospect with Copilot" box is hidden for
            AppSumo (Copilot disabled), which otherwise leaves an empty void
            above "OR ENRICH FROM". Fill it with the theme-aware Cleanlist
            wordmark + slogan so the page leads with brand rather than blank
            space. Scoped to AppSumo so the Copilot composer keeps the top
            slot for everyone else. */}
        {hasAppSumo && (
          <div className='mx-auto flex max-w-xl flex-col items-center gap-2 pt-2 pb-1 text-center'>
            {theme === 'dark' ? (
              <LogoLight className='h-8 w-auto' />
            ) : (
              <Logo className='h-8 w-auto' />
            )}
            <p className='text-sm font-medium tracking-tight text-muted-foreground'>
              Prospect with {BRAND.name}
            </p>
          </div>
        )}

        {/* AI Search Box - on submit, the card "flies" toward the sidebar
            while a ghost of the prompt text races ahead of it. */}
        <div className='relative mx-auto max-w-xl'>
          <motion.div
            animate={
              launchedQuery
                ? {
                    x: '18vw',
                    scale: 0.72,
                    opacity: 0,
                    filter: 'blur(6px)',
                  }
                : { x: 0, scale: 1, opacity: 1, filter: 'blur(0px)' }
            }
            transition={{
              duration: 0.48,
              ease: [0.32, 0.72, 0.24, 1],
            }}
            style={{ transformOrigin: 'right center' }}
          >
            <ChatSearchBox
              onSearch={async (query: string) => {
                const q = query.trim();
                if (!q || launchedQuery) return;
                setLaunchedQuery(query);

                // Interpret the natural-language query through the SAME Copilot
                // agent the in-chat sidebar uses, then open it on the People
                // search page. Previously this was a naive name-vs-company
                // heuristic that dumped firmographic NL like "CTOs at Series B
                // fintech" straight into companyNameFilter (0 results) — a
                // different, broken experience from the real Copilot.
                let encoded: string | null = null;
                let targetTab: 'people' | 'companies' = 'people';
                try {
                  const result = await copilotSearch.search(
                    q,
                    (found) => landingSearchHandoff(found) !== null
                  );
                  if (result?.kind === 'refused') {
                    if (leftPageRef.current) return;
                    announceCopilotSearchRefusal(result, dispatch);
                    setLaunchedQuery(null);
                    return;
                  }
                  // Clu answered it in its own chat: open that chat, never
                  // ask the question again.
                  if (result?.kind === 'chat') {
                    if (leftPageRef.current) return;
                    dispatch(openAiChatConversation(result.conversationId));
                    dispatch(setAiChatOpen(true));
                    setLaunchedQuery(null);
                    return;
                  }
                  // The API's server-validated criteria when the result has
                  // them, else the legacy mapper (CLE-550).
                  const handoff = result ? landingSearchHandoff(result) : null;
                  if (handoff) {
                    encoded = handoff.q;
                    targetTab = handoff.tab;
                  }
                } catch {
                  encoded = null;
                }
                if (leftPageRef.current) return;

                if (encoded) {
                  navigate({
                    to: '/finder',
                    search: { tab: targetTab, q: encoded },
                  });
                  // Dock the Copilot so the conversation survives the handoff.
                  dispatch(setAiChatOpen(true));
                } else {
                  // Ambiguous query, transport error, or criteria the page
                  // cannot take as they are: fall back to the conversational
                  // Copilot so the user isn't stranded.
                  dispatch(setAiChatInitialMessage(query));
                  dispatch(setAiChatOpen(true));
                }
                setLaunchedQuery(null);
              }}
              isSearching={!!launchedQuery}
            />
          </motion.div>

          <AnimatePresence>
            {launchedQuery && (
              <motion.div
                key='prompt-ghost'
                className='pointer-events-none absolute top-[58%] left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-[#3E8BFF] px-4 py-2 text-sm font-medium whitespace-nowrap text-white shadow-lg shadow-[#3E8BFF]/30'
                initial={{ x: 0, y: 0, scale: 0.85, opacity: 0 }}
                animate={{
                  x: ['0vw', '12vw', '42vw'],
                  y: [0, -12, 0],
                  scale: [0.85, 1, 0.4],
                  opacity: [0, 1, 0],
                }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.6,
                  ease: [0.32, 0.72, 0.24, 1],
                  times: [0, 0.4, 1],
                }}
              >
                {launchedQuery.length > 64
                  ? `${launchedQuery.slice(0, 64)}…`
                  : launchedQuery}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className='mx-auto flex max-w-md items-center gap-4 px-4'>
          <div className='h-px flex-1 bg-linear-to-r from-transparent via-border/60 to-transparent' />
          <span className='text-[12px] font-medium text-muted-foreground'>
            Or enrich from
          </span>
          <div className='h-px flex-1 bg-linear-to-r from-transparent via-border/60 to-transparent' />
        </div>

        <div
          className={cn(
            'mx-auto flex max-w-fit items-stretch overflow-hidden rounded-xl',
            'border border-border/40 dark:border-white/6',
            'bg-white/60 dark:bg-white/[0.02]',
            'shadow-xs shadow-black/3'
          )}
        >
          {sourceTiles.map((tile, index) => {
            const requiredFeature = SOURCE_TILE_FEATURE[tile.id];
            const isGated = isTileGated(tile.id);
            const gatedTierLabel = requiredFeature
              ? (TIER_LABELS[FEATURE_REQUIRED_TIER[requiredFeature]] ??
                'Starter')
              : '';
            const targetPanel = optionToPanel[tile.id];
            const usePopover = Boolean(targetPanel) && !isGated;
            const action = getOptionAction(tile.id);
            const isBusy = tile.id === 'people-search' && isCreating;

            const renderIcon = () => {
              if (isBusy) {
                return (
                  <AppIcon
                    icon={iconSpinnerThird}
                    className='h-4 w-4 animate-spin'
                    style={{ color: tile.color }}
                  />
                );
              }
              if (tile.logoType === 'hubspot') {
                return !hubspotLogoFailed ? (
                  <img
                    src={hubspotLogoUrl}
                    alt=''
                    className='h-4 w-4 object-contain'
                    loading='lazy'
                    decoding='async'
                    onError={() => setHubspotLogoFailed(true)}
                  />
                ) : (
                  <span
                    className='text-[10px] leading-none font-bold'
                    style={{ color: tile.color }}
                  >
                    HS
                  </span>
                );
              }
              if (tile.logoType === 'linkedin') {
                return (
                  <LinkedinLogoIcon
                    className='h-[18px] w-[18px]'
                    style={{ color: tile.color }}
                  />
                );
              }
              if (tile.logoType === 'sales-nav') {
                return (
                  <div className='relative mr-0.5'>
                    <LinkedinLogoIcon
                      className='h-[18px] w-[18px]'
                      style={{ color: tile.color }}
                    />
                    <span
                      className='absolute -right-1.5 -bottom-0.5 rounded-[2px] px-0.5 text-[6px] leading-tight font-bold text-white'
                      style={{ backgroundColor: tile.color }}
                    >
                      SN
                    </span>
                  </div>
                );
              }
              if (tile.logoType === 'csv') {
                return (
                  <CsvLogoIcon
                    className='h-[18px] w-[18px]'
                    style={{ color: tile.color }}
                  />
                );
              }
              if (tile.icon) {
                return (
                  <tile.icon
                    className='h-4 w-4'
                    style={{ color: tile.color }}
                  />
                );
              }
              return null;
            };

            const tileButton = (
              <button
                key={tile.id}
                type='button'
                disabled={isBusy}
                aria-busy={isBusy || undefined}
                onClick={
                  isGated
                    ? () =>
                        navigate({
                          to: '/plans',
                          search: {
                            status: undefined,
                            credits_added: undefined,
                            plan_name: undefined,
                          },
                        })
                    : usePopover
                      ? undefined
                      : () => void action()
                }
                className={cn(
                  'group relative flex min-w-0 items-center gap-1.5 px-3 py-2.5 xl:gap-2.5 xl:px-5 xl:py-3',
                  'transition-colors duration-150 disabled:cursor-wait',
                  isGated
                    ? 'cursor-pointer opacity-50 hover:opacity-70'
                    : 'hover:bg-black/4 dark:hover:bg-white/[0.05]',
                  index > 0 && 'border-l border-border/30 dark:border-white/6'
                )}
              >
                <span className='relative flex shrink-0 items-center justify-center'>
                  {renderIcon()}
                </span>
                <span
                  className={cn(
                    'relative truncate text-xs font-medium transition-colors duration-150 xl:text-[13px]',
                    isGated
                      ? 'text-muted-foreground/40'
                      : 'text-muted-foreground/70 group-hover:text-foreground'
                  )}
                >
                  {tile.label}
                </span>
                {isGated && (
                  <AppIcon
                    icon={iconCircleArrowUp}
                    className='h-3.5 w-3.5 shrink-0 text-muted-foreground/50'
                  />
                )}
              </button>
            );

            if (isGated) {
              return (
                <TooltipProvider key={tile.id} delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>{tileButton}</TooltipTrigger>
                    <TooltipContent side='bottom'>
                      <p className='text-xs'>
                        Available on {gatedTierLabel}+ - Click to upgrade
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            }

            if (usePopover && targetPanel) {
              return (
                <ListSelectionPopover key={tile.id} targetPanel={targetPanel}>
                  {tileButton}
                </ListSelectionPopover>
              );
            }

            return tileButton;
          })}
        </div>
      </div>

      <AlertDialog open={hubspotDialogOpen} onOpenChange={setHubspotDialogOpen}>
        <AlertDialogContent
          className={cn(
            'max-w-md overflow-hidden p-0',
            'backdrop-blur-xl',
            'border border-black/5 dark:border-white/8'
          )}
        >
          <AlertDialogHeader className='border-b border-border/50 p-6 pb-4'>
            <div className='flex items-center gap-3'>
              <div
                className={cn(
                  'rounded-lg p-2.5 shadow-lg',
                  'bg-brand-hubspot/10 shadow-brand-hubspot/20'
                )}
              >
                <AppIcon
                  icon={iconLink}
                  className='h-5 w-5 text-brand-hubspot'
                />
              </div>
              <div>
                <AlertDialogTitle className='text-lg font-semibold text-foreground'>
                  Connect HubSpot
                </AlertDialogTitle>
                <AlertDialogDescription className='text-sm text-muted-foreground'>
                  Required to import marketing lists
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>

          <div className='p-6'>
            <div
              className={cn(
                'rounded-xl border p-4',
                'bg-white/70 dark:bg-white/[0.02]',
                'border-border/50'
              )}
            >
              <p className='text-sm text-muted-foreground'>
                Connect your HubSpot account in Integrations before importing a
                marketing list. This allows {BRAND.name} to access your contact
                lists.
              </p>
            </div>
          </div>

          <AlertDialogFooter className='border-t border-border/50 p-4'>
            <AlertDialogCancel className='rounded-lg'>
              Not now
            </AlertDialogCancel>
            <AlertDialogAction
              variant='brand'
              className='rounded-lg bg-brand-hubspot text-white hover:bg-brand-hubspot/90'
              onClick={() => navigate({ to: '/integrations' })}
            >
              <AppIcon icon={iconLink} className='mr-2 h-4 w-4' />
              Go to Integrations
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={extensionDialogMode !== null}
        onOpenChange={(open) => {
          if (!open) {
            setExtensionDialogMode(null);
          }
        }}
      >
        <AlertDialogContent
          className={cn(
            'max-w-md overflow-hidden p-0',
            'backdrop-blur-xl',
            'border border-black/5 dark:border-white/8'
          )}
        >
          <AlertDialogHeader className='border-b border-border/50 p-6 pb-4'>
            <div className='flex items-center gap-3'>
              <div
                className={cn(
                  'rounded-lg p-2.5 shadow-lg',
                  extensionDialogMode === 'install'
                    ? 'bg-blue-500/10 shadow-blue-500/20'
                    : 'bg-brand-lemlist/10 shadow-brand-lemlist/20'
                )}
              >
                {extensionDialogMode === 'install' ? (
                  <AppIcon
                    icon={iconChrome}
                    className='h-5 w-5 text-blue-500'
                  />
                ) : (
                  <AppIcon
                    icon={iconKey}
                    className='h-5 w-5 text-brand-lemlist'
                  />
                )}
              </div>
              <div>
                <AlertDialogTitle className='text-lg font-semibold text-foreground'>
                  {extensionDialogContent?.title}
                </AlertDialogTitle>
                <AlertDialogDescription className='text-sm text-muted-foreground'>
                  {extensionDialogMode === 'install'
                    ? 'Required for LinkedIn prospecting'
                    : 'Link your account to the extension'}
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>

          <div className='space-y-4 p-6'>
            {extensionDialogMode === 'install' ? (
              <>
                <p className='text-sm text-muted-foreground'>
                  The {BRAND.name} Chrome extension enables powerful LinkedIn
                  prospecting features.
                </p>
                <div className='space-y-2'>
                  {[
                    {
                      icon: Download,
                      text: 'Install from Chrome Web Store',
                      tone: 'bg-blue-500/10 text-blue-500',
                    },
                    {
                      icon: Zap,
                      text: 'Capture leads directly from LinkedIn',
                      tone: 'bg-emerald-500/10 text-emerald-500',
                    },
                    {
                      icon: Settings,
                      text: `Connect with your ${BRAND.name} account`,
                      tone: 'bg-brand-lemlist/10 text-brand-lemlist',
                    },
                  ].map((item, index) => {
                    const [bgClass, textClass] = item.tone.split(' ');
                    return (
                      <div
                        key={index}
                        className={cn(
                          'flex items-center gap-3 rounded-lg p-3',
                          'bg-white/70 dark:bg-white/[0.02]',
                          'border border-border/50'
                        )}
                      >
                        <div
                          className={cn(
                            'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                            bgClass
                          )}
                        >
                          <item.icon className={cn('h-4 w-4', textClass)} />
                        </div>
                        <span className='text-sm font-medium'>{item.text}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                <p className='text-sm text-muted-foreground'>
                  Complete the setup by connecting your API key to the
                  extension.
                </p>
                <div className='space-y-2'>
                  {[
                    {
                      step: '1',
                      text: 'Generate an API key in your account',
                      bgClass: 'bg-brand-lemlist',
                    },
                    {
                      step: '2',
                      text: 'Open the extension settings',
                      bgClass: 'bg-brand-instantly',
                    },
                    {
                      step: '3',
                      text: 'Paste your API key and save',
                      bgClass: 'bg-emerald-500',
                    },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className={cn(
                        'flex items-center gap-3 rounded-lg p-3',
                        'bg-white/70 dark:bg-white/[0.02]',
                        'border border-border/50'
                      )}
                    >
                      <div
                        className={cn(
                          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white',
                          item.bgClass
                        )}
                      >
                        {item.step}
                      </div>
                      <span className='text-sm font-medium'>{item.text}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <AlertDialogFooter className='border-t border-border/50 p-4'>
            <AlertDialogCancel className='rounded-lg'>
              Not now
            </AlertDialogCancel>
            <AlertDialogAction
              variant={extensionDialogMode === 'install' ? 'default' : 'brand'}
              className={cn(
                'rounded-lg',
                extensionDialogMode !== 'install' &&
                  'bg-brand-lemlist hover:bg-brand-lemlist/90'
              )}
              onClick={() => {
                extensionDialogContent?.action();
                setExtensionDialogMode(null);
              }}
            >
              {extensionDialogMode === 'install' ? (
                <AppIcon icon={iconChrome} className='mr-2 h-4 w-4' />
              ) : (
                <AppIcon icon={iconKey} className='mr-2 h-4 w-4' />
              )}
              {extensionDialogContent?.actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={newListDialogOpen}
        onOpenChange={(open) => setNewListDialogOpen(open)}
      >
        <DialogContent
          className={cn(
            'max-w-md overflow-hidden p-0',
            'backdrop-blur-xl',
            'border border-black/5 dark:border-white/8'
          )}
        >
          <DialogHeader className='border-b border-border/50 p-6 pb-4'>
            <div className='flex items-center gap-3'>
              <div
                className={cn(
                  'rounded-lg p-2.5 shadow-lg',
                  'bg-emerald-500/10 shadow-emerald-500/20'
                )}
              >
                <AppIcon
                  icon={iconFolderPlus}
                  className='h-5 w-5 text-emerald-500'
                />
              </div>
              <div>
                <DialogTitle className='text-lg font-semibold text-foreground'>
                  Create New List
                </DialogTitle>
                <DialogDescription className='text-sm text-muted-foreground'>
                  Give this list a name before adding prospects
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <form
            className='p-6'
            onSubmit={(event) => {
              event.preventDefault();
              if (!newListName.trim() || isCreating) {
                return;
              }
              handleCreateNewList();
            }}
          >
            <div className='space-y-2'>
              <Label
                htmlFor='newListNameInput'
                className='text-[12px] font-medium text-muted-foreground'
              >
                List name
              </Label>
              <Input
                id='newListNameInput'
                value={newListName}
                onChange={(event) => setNewListName(event.target.value)}
                placeholder='e.g. Q1 Outbound Targets'
                autoFocus
                className={cn(
                  'h-11 rounded-lg',
                  'bg-white/70 dark:bg-white/[0.02]',
                  'border-border/50 focus:border-primary/30'
                )}
              />
            </div>
            <DialogFooter className='mt-6 border-t border-border/50 pt-4'>
              <Button
                type='button'
                variant='ghost'
                onClick={() => setNewListDialogOpen(false)}
                className='rounded-lg'
              >
                Cancel
              </Button>
              <Button
                type='submit'
                className='min-w-[120px] rounded-lg'
                disabled={!newListName.trim() || isCreating}
              >
                {isCreating ? (
                  <>
                    <AppIcon
                      icon={iconSpinnerThird}
                      className='mr-2 animate-spin'
                      style={{ fontSize: 16 }}
                    />
                    Creating...
                  </>
                ) : (
                  <>
                    <AppIcon icon={iconCircleCheck} className='mr-2 h-4 w-4' />
                    Continue
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={findProspectsDialogOpen}
        onOpenChange={(open) => setFindProspectsDialogOpen(open)}
      >
        <DialogContent
          className={cn(
            'max-w-lg overflow-hidden p-0',
            'backdrop-blur-xl',
            'border border-black/5 dark:border-white/8'
          )}
        >
          <DialogHeader className='border-b border-border/50 p-6 pb-4'>
            <div className='flex items-center gap-3'>
              <div
                className={cn(
                  'rounded-lg p-2.5 shadow-lg',
                  'bg-primary/10 shadow-primary/20'
                )}
              >
                <AppIcon
                  icon={iconWandMagicSparkles}
                  className='h-5 w-5 text-primary'
                />
              </div>
              <div>
                <DialogTitle className='text-lg font-semibold text-foreground'>
                  Add leads to {newListName}
                </DialogTitle>
                <DialogDescription className='text-sm text-muted-foreground'>
                  Choose a source to import prospects
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className='p-6'>
            <div className='flex flex-wrap items-center justify-center gap-4'>
              {sourceTiles.map((tile) => {
                const isGated = isTileGated(tile.id);
                const action = getOptionAction(
                  tile.id,
                  newListId || undefined,
                  () => setFindProspectsDialogOpen(false)
                );

                return (
                  <button
                    key={tile.id}
                    type='button'
                    onClick={
                      isGated
                        ? () =>
                            navigate({
                              to: '/plans',
                              search: {
                                status: undefined,
                                credits_added: undefined,
                                plan_name: undefined,
                              },
                            })
                        : () => void action()
                    }
                    className={cn(
                      'group relative flex flex-col items-center gap-3',
                      isGated && 'opacity-50'
                    )}
                  >
                    <div
                      className='pointer-events-none absolute -inset-3 rounded-3xl opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-100'
                      style={{
                        background: `radial-gradient(circle, ${tile.color}20 0%, transparent 70%)`,
                      }}
                    />
                    <div
                      className={cn(
                        'relative flex h-16 w-16 items-center justify-center rounded-2xl',
                        'border border-border/40 dark:border-white/6',
                        'bg-linear-to-b from-white to-gray-50/80 dark:from-white/4 dark:to-white/2',
                        'shadow-xs shadow-black/3',
                        'transition-all duration-300 ease-out',
                        'group-hover:scale-105 group-hover:border-transparent group-hover:shadow-lg',
                        'group-active:scale-100'
                      )}
                    >
                      <div className='pointer-events-none absolute inset-px rounded-[15px] bg-linear-to-b from-white/80 to-transparent opacity-60 dark:from-white/8' />

                      <div
                        className='pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100'
                        style={{
                          background: `linear-gradient(135deg, ${tile.color}30 0%, ${tile.color}10 100%)`,
                          padding: '1px',
                          WebkitMask:
                            'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                          WebkitMaskComposite: 'xor',
                          maskComposite: 'exclude',
                        }}
                      />

                      {tile.logoType === 'hubspot' && (
                        <>
                          {!hubspotLogoFailed ? (
                            <img
                              src={hubspotLogoUrl}
                              alt='HubSpot'
                              className='relative h-9 w-9 object-contain transition-transform duration-300 group-hover:scale-110'
                              loading='lazy'
                              decoding='async'
                              onError={() => setHubspotLogoFailed(true)}
                            />
                          ) : (
                            <span
                              className='relative text-sm font-bold'
                              style={{ color: tile.color }}
                            >
                              HS
                            </span>
                          )}
                        </>
                      )}
                      {tile.logoType === 'linkedin' && (
                        <LinkedinLogoIcon
                          className='relative h-10 w-10 transition-transform duration-300 group-hover:scale-110'
                          style={{ color: tile.color }}
                        />
                      )}
                      {tile.logoType === 'sales-nav' && (
                        <div className='relative flex h-10 w-10 items-center justify-center transition-transform duration-300 group-hover:scale-110'>
                          <LinkedinLogoIcon
                            className='h-10 w-10'
                            style={{ color: tile.color }}
                          />
                          <span
                            className='absolute -right-2.5 -bottom-0.5 rounded-sm px-1 text-[8px] font-bold text-white shadow-xs'
                            style={{ backgroundColor: tile.color }}
                          >
                            SN
                          </span>
                        </div>
                      )}
                      {tile.icon && !tile.logoType && (
                        <tile.icon
                          className={cn(
                            'relative transition-transform duration-300 group-hover:scale-110',
                            tile.id === 'manual' ? 'h-7 w-7' : 'h-9 w-9'
                          )}
                          style={{ color: tile.color }}
                        />
                      )}
                    </div>
                    <span className='text-xs font-medium text-muted-foreground/70 transition-colors duration-300 group-hover:text-foreground'>
                      {tile.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <DialogFooter className='border-t border-border/50 p-4'>
            <Button
              variant='ghost'
              onClick={() => setFindProspectsDialogOpen(false)}
              className='rounded-lg'
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
